import type {
  Heat,
  ObjectionType,
  Outcome,
  PlaybookChannel,
  PlaybookVariant,
  SpokeWith,
  TouchChannel,
} from '../../../shared/enums';
import { HEATS, OBJECTION_TYPES, OUTCOMES, SPOKE_WITH, TOUCH_CHANNELS } from '../../../shared/enums';
import { personaForBusinessType } from '../../../shared/playbook';
import type { Lead, PlaybookMessage } from '../../../shared/types';
import { shell } from '../app';
import { createDraft } from '../data/drafts';
import { listFeatures, listPlaybook, logVisit } from '../data/store';
import {
  btn,
  chips,
  dateQuickPicks,
  heatDot,
  input,
  section,
  segmented,
  select,
  stageTag,
  textarea,
} from '../ui/components';
import { h, replaceChildren } from '../ui/dom';
import {
  CHANNEL_LABELS,
  HEAT_LABELS,
  OBJECTION_LABELS,
  OUTCOME_LABELS,
  SPOKE_WITH_LABELS,
} from '../ui/labels';
import { featurePicker } from '../ui/featurePicker';
import { navigate } from '../ui/router';

/** Quick-log step 2: the ~20-second visit form (big buttons → notes → objections → next action). */
// ---------------------------------------------------------------- step 2: the log form

export const logStep = async (
  host: HTMLElement,
  lead: Lead,
  opts: { onChangeLead: (() => void) | null; returnTo: string },
) => {
  let spokeWith: SpokeWith | null = null;
  let outcome: Outcome | null = null;
  let notes = '';
  let heat: Heat = lead.heat;
  const objections = new Set<ObjectionType>();
  const objectionDetails = new Map<ObjectionType, string>();
  const knownFeatures = await listFeatures();
  const picker = featurePicker(knownFeatures);
  let nextAction = '';
  let nextActionDate: string | null = null;
  let nextActionTime: string | null = null;
  // D90: a touch of any kind. A walk-in is the default, so the 20-second flow costs no extra tap.
  let channel: TouchChannel = 'walk_in';
  let variant: PlaybookVariant | null = null;
  const persona = personaForBusinessType(lead.business_type);
  const library = await listPlaybook();

  const saveLabel = () => `Save ${CHANNEL_LABELS[channel].toLowerCase()}`;
  const save = btn(saveLabel(), { variant: 'primary', size: 'lg', class: 'w-full', disabled: true });
  const refreshSave = () => {
    save.disabled = !(spokeWith && outcome);
  };

  /** The library messages for this door on this channel (D88); a voicemail is what a call leaves. */
  const messagesFor = (c: TouchChannel): PlaybookMessage[] => {
    const wanted: PlaybookChannel | null =
      c === 'walk_in' ? 'walk_in' : c === 'email' ? 'email' : c === 'linkedin' ? 'linkedin' : c === 'call' || c === 'voicemail' ? 'call' : null;
    if (!wanted) return [];
    return library
      .filter((m) => m.persona === persona && m.channel === wanted)
      .sort((a, b) => a.sort - b.sort);
  };

  const variantHost = h('div', { class: 'logvariant' });
  const renderVariant = () => {
    const options = messagesFor(channel);
    variant = null;
    replaceChildren(
      variantHost,
      options.length === 0
        ? null
        : select<string>({
            value: '',
            options: [
              { value: '', label: 'Message used — none' },
              ...options.map((m) => ({ value: m.variant, label: m.title })),
            ],
            onChange: (v) => (variant = (v || null) as PlaybookVariant | null),
            ariaLabel: 'Message used',
          }),
    );
  };

  // One detail input per selected objection, in the order they were picked.
  const detailsHost = h('div', { class: 'objdetails' });
  const renderDetails = () => {
    replaceChildren(
      detailsHost,
      [...objections].map((t) =>
        input({
          placeholder: `${OBJECTION_LABELS[t]} — detail (optional)`,
          maxlength: 1000,
          value: objectionDetails.get(t) ?? '',
          class: 'input--sm',
          onInput: (v) => objectionDetails.set(t, v),
        }),
      ),
    );
  };

  save.addEventListener('click', async () => {
    if (!spokeWith || !outcome) return;
    save.disabled = true;
    save.textContent = 'Saving…';
    try {
      await logVisit({
        lead_id: lead.id,
        spoke_with: spokeWith,
        outcome,
        notes,
        heat,
        channel,
        variant,
        next_action: nextAction,
        next_action_date: nextActionDate,
        next_action_time: nextActionTime,
        objections: [...objections].map((type) => ({ type, detail: objectionDetails.get(type) ?? null })),
        features: picker.getAsks(),
      });
      // always offered: navigator.onLine is not trusted (D68), and createDraft reports a real failure
      shell.toast('Visit logged — tap to draft the follow-up email', {
        kind: 'success',
        duration: 6000,
        onClick: () => {
          void createDraft(lead.id, 'post_visit')
            .then(() => navigate('#/emails'))
            .catch((err: unknown) =>
              shell.toast(err instanceof Error ? err.message : String(err), { kind: 'error' }),
            );
        },
      });
      navigate(opts.returnTo, { replace: true });
    } catch (err) {
      shell.toast(`Could not save: ${err instanceof Error ? err.message : String(err)}`, { kind: 'error' });
      save.disabled = false;
      save.textContent = saveLabel();
    }
  });

  replaceChildren(
    host,
    h(
      'div',
      { class: 'page page--log' },
      h(
        'div',
        { class: 'leadcard' },
        h(
          'div',
          { class: 'leadcard__text' },
          h('div', { class: 'leadcard__name' }, heatDot(lead.heat), ' ', lead.business_name),
          h(
            'div',
            { class: 'leadcard__meta' },
            stageTag(lead.stage),
            lead.neighborhood ? ` · ${lead.neighborhood}` : '',
            lead.owner_name ? ` · ${lead.owner_name}` : '',
          ),
        ),
        opts.onChangeLead
          ? btn('Change', { variant: 'subtle', size: 'sm', onClick: opts.onChangeLead })
          : null,
      ),
      section(
        { title: 'How' },
        segmented<TouchChannel>({
          options: TOUCH_CHANNELS.map((c) => ({ value: c, label: CHANNEL_LABELS[c] })),
          value: channel,
          onChange: (v) => {
            channel = v;
            save.textContent = saveLabel();
            renderVariant();
          },
          columns: 4,
          size: 'md',
        }),
        variantHost,
      ),
      section(
        { title: 'Spoke with' },
        segmented<SpokeWith>({
          options: SPOKE_WITH.map((s) => ({ value: s, label: SPOKE_WITH_LABELS[s] })),
          value: null,
          onChange: (v) => {
            spokeWith = v;
            refreshSave();
          },
          columns: 2,
        }),
      ),
      section(
        { title: 'Outcome' },
        segmented<Outcome>({
          options: OUTCOMES.map((o) => ({ value: o, label: OUTCOME_LABELS[o] })),
          value: null,
          onChange: (v) => {
            outcome = v;
            refreshSave();
          },
          columns: 2,
        }),
      ),
      section(
        { title: 'Notes' },
        textarea({
          placeholder: 'Tap the mic on your keyboard and just talk…',
          maxlength: 8000,
          rows: 5,
          onInput: (v) => (notes = v),
          class: 'textarea--big',
        }),
      ),
      section(
        { title: 'Heat' },
        segmented<Heat>({
          options: HEATS.map((hh) => ({ value: hh, label: HEAT_LABELS[hh] })),
          value: heat,
          onChange: (v) => (heat = v),
          columns: 3,
          size: 'md',
        }),
      ),
      section(
        { title: 'Objections' },
        chips<ObjectionType>({
          options: OBJECTION_TYPES.map((t) => ({ value: t, label: OBJECTION_LABELS[t] })),
          selected: objections,
          onToggle: (v, on) => {
            if (on) objections.add(v);
            else objections.delete(v);
            renderDetails();
          },
        }),
        detailsHost,
      ),
      section({ title: 'Requested features' }, picker.el),
      section(
        { title: 'Next action' },
        input({
          placeholder: 'e.g. Bring rate comparison',
          maxlength: 200,
          autocapitalize: 'sentences',
          enterkeyhint: 'done',
          onInput: (v) => (nextAction = v),
        }),
        dateQuickPicks({
          value: null,
          onChange: (v) => (nextActionDate = v),
          onTimeChange: (v) => (nextActionTime = v),
        }),
      ),
      h('div', { class: 'actionbar' }, save),
    ),
  );
  renderVariant();
};
